import type { CompanyAdmin } from "@dutyon/apis";
import { Button, Group, Paper, Stack, Switch, Table, Text, Title } from "@dutyon/ui-kit";
import { Sparkles, Trash } from "lucide-react";

interface CompanySettingsProps {
  admins?: CompanyAdmin[];
  onAddAdmin: () => void;
  onRemoveAdmin: (userId: string) => void;
  isRemovingAdmin: boolean;
  dutyAutoGenerationEnabled: boolean;
  onToggleDutyAutoGeneration: (enabled: boolean) => void;
  isTogglingDutyAutoGeneration: boolean;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

export function CompanySettings({
  admins,
  onAddAdmin,
  onRemoveAdmin,
  isRemovingAdmin,
  dutyAutoGenerationEnabled,
  onToggleDutyAutoGeneration,
  isTogglingDutyAutoGeneration,
  onEdit,
  onDelete,
  isDeleting,
}: CompanySettingsProps) {
  return (
    <Stack gap="lg">
      <Paper withBorder p="md">
        <Group justify="space-between" align="center">
          <Group>
            <Sparkles size={20} color="var(--mantine-color-violet-6)" />
            <div>
              <Text fw={600}>Duty 자동 생성 기능</Text>
              <Text size="xs" c="dimmed" mt={4}>
                활성화하면 해당 병원의 관리자가 대시보드에서 Duty를 자동으로 생성할 수 있습니다.
              </Text>
            </div>
          </Group>
          <Switch
            checked={dutyAutoGenerationEnabled}
            disabled={isTogglingDutyAutoGeneration}
            onChange={(e) => onToggleDutyAutoGeneration(e.currentTarget.checked)}
            aria-label="Duty 자동 생성 기능 활성화"
          />
        </Group>
      </Paper>
      <Paper withBorder p="md">
        <Group justify="space-between" mb="md">
          <Title order={4}>병원 관리자</Title>
          <Button size="xs" onClick={onAddAdmin}>
            관리자 추가
          </Button>
        </Group>

        {!admins || admins.length === 0 ? (
          <Text c="dimmed">등록된 관리자가 없습니다.</Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>이름</Table.Th>
                <Table.Th>이메일</Table.Th>
                <Table.Th>작업</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {admins.map((admin) => (
                <Table.Tr key={admin.id}>
                  <Table.Td>{admin.name}</Table.Td>
                  <Table.Td>{admin.email}</Table.Td>
                  <Table.Td>
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      aria-label={`${admin.name} 관리자 권한 해제`}
                      onClick={() => onRemoveAdmin(admin.id)}
                      loading={isRemovingAdmin}
                    >
                      <Trash size={16} />
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Paper withBorder p="lg" style={{ borderColor: "#ffc9c9", backgroundColor: "#fff5f5" }}>
        <Title order={4} mb="md" c="red">
          Danger Zone
        </Title>
        <Group justify="space-between" align="center">
          <div>
            <Text size="sm" fw={500}>
              병원 정보 수정
            </Text>
            <Text size="xs" c="dimmed">
              병원의 이름이나 코드를 수정합니다.
            </Text>
          </div>
          <Button variant="outline" color="red" onClick={onEdit}>
            수정하기
          </Button>
        </Group>
        <Group justify="space-between" align="center" mt="md">
          <div>
            <Text size="sm" fw={500}>
              병원 삭제
            </Text>
            <Text size="xs" c="dimmed">
              병원을 영구적으로 삭제합니다. 이 작업은 되돌릴 수 없습니다.
            </Text>
          </div>
          <Button color="red" variant="filled" onClick={onDelete} loading={isDeleting}>
            삭제하기
          </Button>
        </Group>
      </Paper>
    </Stack>
  );
}
